import type { ComponentPropsWithoutRef, CSSProperties, FC, MouseEvent, ReactNode } from 'react';
import {
  Children,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Components, ExtraProps } from 'react-markdown';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism';
import { msg } from '@gonfalon/messages/msg';
import { toSession } from '@gonfalon/navigator/navigators';
import clsx from 'clsx';
import remarkGfm from 'remark-gfm';

import { ArtifactChip } from '../../../components/Artifact';
import { getArtifactTypeLabel } from '../Artifact/artifactDisplay';

import { useArtifactLookup } from './ArtifactLookupContext';
import { parseTaskIndex, parseTaskLabel, rehypeTaskListIndices } from './rehypeTaskListIndices';
import type { VegaExternalSourceMatch } from './sourceUrlUtils';
import { matchVegaExternalSourceUrl } from './sourceUrlUtils';

import styles from './MarkdownRenderer.module.css';

const rehypePlugins = [rehypeTaskListIndices];
const remarkPlugins = [remarkGfm];

/**
 * Quiet Prism theme — CSS variables with hex fallbacks so Storybook (and any
 * surface missing the observability theme sheet) still gets readable token colors.
 */
const quietPrismTheme: Record<string, CSSProperties> = {
  'code[class*="language-"]': {
    color: 'var(--lp-color-text-ui-primary-base)',
    background: 'transparent',
    fontFamily: 'var(--lp-font-family-monospace, monospace)',
    fontSize: 10,
    lineHeight: 1.4,
    textShadow: 'none',
  },
  'pre[class*="language-"]': {
    color: 'var(--lp-color-text-ui-primary-base)',
    background: 'transparent',
    margin: 0,
    padding: 0,
    fontFamily: 'var(--lp-font-family-monospace, monospace)',
    fontSize: 10,
    lineHeight: 1.4,
    textShadow: 'none',
  },
  comment: { color: 'var(--lp-color-text-ui-secondary)' },
  prolog: { color: 'var(--lp-color-text-ui-secondary)' },
  doctype: { color: 'var(--lp-color-text-ui-secondary)' },
  cdata: { color: 'var(--lp-color-text-ui-secondary)' },
  punctuation: { color: 'var(--lp-color-text-ui-secondary)' },
  property: { color: 'var(--lp-color-text-interactive-base)' },
  tag: { color: 'var(--lp-color-text-interactive-base)' },
  boolean: { color: 'var(--lp-color-text-ui-primary-base)', fontWeight: 600 },
  number: { color: 'var(--lp-color-text-ui-primary-base)' },
  constant: { color: 'var(--lp-color-text-ui-primary-base)', fontWeight: 600 },
  symbol: { color: 'var(--lp-color-text-ui-primary-base)' },
  deleted: { color: 'var(--lp-color-text-ui-primary-base)' },
  selector: { color: 'var(--lp-color-text-interactive-base)' },
  'attr-name': { color: 'var(--lp-color-text-interactive-base)' },
  string: { color: 'var(--lp-color-text-feedback-success)' },
  char: { color: 'var(--lp-color-text-feedback-success)' },
  builtin: { color: 'var(--lp-color-text-ui-primary-base)', fontWeight: 600 },
  inserted: { color: 'var(--lp-color-text-feedback-success)' },
  operator: { color: 'var(--lp-color-text-ui-secondary)' },
  entity: { color: 'var(--lp-color-text-ui-primary-base)' },
  url: { color: 'var(--lp-color-text-ui-primary-base)' },
  atrule: { color: 'var(--lp-color-text-ui-primary-base)', fontWeight: 600 },
  'attr-value': { color: 'var(--lp-color-text-feedback-success)' },
  keyword: { color: 'var(--lp-color-text-ui-primary-base)', fontWeight: 600 },
  function: { color: 'var(--lp-color-text-ui-primary-base)' },
  'class-name': { color: 'var(--lp-color-text-ui-primary-base)' },
  regex: { color: 'var(--lp-color-text-ui-primary-base)' },
  important: { color: 'var(--lp-color-text-ui-primary-base)', fontWeight: 600 },
  variable: { color: 'var(--lp-color-text-ui-primary-base)' },
};

// Match <session-id>...</session-id> for linkification (session IDs are alphanumeric)
const SESSION_ID_TAG_REGEX = /<session-id>([^<]+)<\/session-id>/g;
const SESSION_LINK_PROTOCOL = 'session://';

// Match :::artifact{type=X,external_id=Y} tokens emitted by Vega. external_id is
// expected URL-encoded so it can contain `/`, `:`, and `=` without breaking the regex.
const ARTIFACT_TOKEN_REGEX = /:::artifact\{type=([a-z_]+),external_id=([^}]+)\}/g;
const VEGA_ARTIFACT_LINK_PROTOCOL = 'vega-artifact://';

// Strip :::hidden{...} single-line directives emitted by Vega skills.
// Single-line sentinels: :::hidden{feedback:slug}
const HIDDEN_LINE_REGEX = /:::hidden\{[^}\n]*\}[^\n]*\n?/g;

const stripHiddenDirectives = (text: string): string => text.replace(HIDDEN_LINE_REGEX, '');

/**
 * Pre-process text to convert <session-id>xxx</session-id> tags into markdown links
 * that can be handled by the custom link component.
 */
const preprocessSessionIds = (text: string): string => {
  return text.replace(SESSION_ID_TAG_REGEX, (_, sessionId: string) => {
    const trimmed = sessionId.trim();
    return `[${trimmed}](${SESSION_LINK_PROTOCOL}${trimmed})`;
  });
};

/**
 * Pre-process :::artifact{} tokens into markdown links of the form
 *   [Type label: externalId](vega-artifact://<type>/<encoded externalId>)
 * The custom `a:` component swaps the anchor for an ArtifactChip.
 */
const preprocessArtifactTokens = (text: string): string => {
  return text.replace(ARTIFACT_TOKEN_REGEX, (_, type: string, encodedExternalId: string) => {
    let decoded: string;
    try {
      decoded = decodeURIComponent(encodedExternalId);
    } catch {
      decoded = encodedExternalId;
    }
    const label = `${getArtifactTypeLabel(type)}: ${decoded}`;
    const safeLabel = label.replace(/\]/g, '\\]');
    return `[${safeLabel}](${VEGA_ARTIFACT_LINK_PROTOCOL}${type}/${encodedExternalId})`;
  });
};

interface ParsedArtifactHref {
  type: string;
  externalId: string;
}

const parseArtifactHref = (href: string): ParsedArtifactHref | null => {
  if (!href.startsWith(VEGA_ARTIFACT_LINK_PROTOCOL)) {
    return null;
  }
  const rest = href.slice(VEGA_ARTIFACT_LINK_PROTOCOL.length);
  const slash = rest.indexOf('/');
  if (slash <= 0 || slash === rest.length - 1) {
    return null;
  }
  const type = rest.slice(0, slash);
  const encoded = rest.slice(slash + 1);
  try {
    return { type, externalId: decodeURIComponent(encoded) };
  } catch {
    return { type, externalId: encoded };
  }
};

export type RenderArtifactTokenArgs = {
  projectKey: string;
  type: string;
  externalId: string;
};

export interface MarkdownRendererProps {
  text: string;
  projectKey: string;
  onExternalSourceClick?: (match: VegaExternalSourceMatch) => void;
  // When true, :::artifact{} tokens render as ArtifactChips (hydrated from the surrounding
  // ArtifactLookupProvider if present, otherwise via renderArtifactToken). When false
  // (default), tokens render as plain text. Gated by the consuming surface — typically
  // vegaArtifactGraph().
  artifactsEnabled?: boolean;
  // Optional consumer-provided renderer for artifact tokens that aren't in the lookup
  // context. Receives the parsed type and externalId; returns a ReactNode (typically a
  // chip wired to a lazy-fetch hook). When omitted, unresolved tokens render as a
  // placeholder ArtifactChip. Lives at the consumer because lazy-fetch requires a
  // dependency (observability-graphql) that this UI package deliberately doesn't pull in.
  renderArtifactToken?: (args: RenderArtifactTokenArgs) => ReactNode;
  onTaskAction?: (params: { action: 'toggle'; index: number; label: string } | { action: 'reset' }) => void;
  // Optional policy hook for generic external links in the rendered markdown. When provided, any
  // link whose href fails this predicate is rendered as inert text (no anchor) instead of a
  // clickable link. Consumers rendering attacker-influenceable content (e.g. agent responses)
  // pass a host/scheme allow-list here so the content can't link a user to an arbitrary site.
  // When omitted, all links render normally (react-markdown still strips dangerous schemes).
  isExternalUrlAllowed?: (href: string | null | undefined) => boolean;
}

type MarkdownCallbacks = Pick<
  MarkdownRendererProps,
  'onExternalSourceClick' | 'renderArtifactToken' | 'onTaskAction' | 'isExternalUrlAllowed'
>;

interface MarkdownRuntime {
  projectKey: string;
  artifactsEnabled: boolean;
  callbacks: MarkdownCallbacks;
  checkedTasks: Record<number, boolean>;
  setTaskChecked: (index: number, checked: boolean) => void;
}

/**
 * Everything the overridden renderers below need that changes between renders — props, the
 * consumer callbacks (passed inline, so their identities churn), and checkbox state.
 *
 * The renderers are module-level components rather than closures rebuilt inside
 * `MarkdownRenderer`, so react-markdown sees the same element types forever and never tears the
 * rendered tree down. Reading the changing values from context (not from refs written during
 * render) keeps them correct under concurrent rendering: a render that React abandons never
 * installs its callbacks over the committed ones.
 */
const MarkdownRuntimeContext = createContext<MarkdownRuntime | null>(null);

const useMarkdownRuntime = (): MarkdownRuntime => {
  const runtime = useContext(MarkdownRuntimeContext);
  if (!runtime) {
    throw new Error('Markdown renderers must be rendered inside MarkdownRenderer');
  }
  return runtime;
};

// Table: wrap in a scrollable container for narrow panels
const MarkdownTable = ({
  children,
  className,
  node: _node,
  ...rest
}: ComponentPropsWithoutRef<'table'> & ExtraProps) => (
  <div className={styles.tableWrapper}>
    <div className={styles.tableScroller}>
      <table className={clsx(styles.table, className)} {...rest}>
        {children}
      </table>
    </div>
  </div>
);

const MarkdownThead = ({
  children,
  className,
  node: _node,
  ...rest
}: ComponentPropsWithoutRef<'thead'> & ExtraProps) => (
  <thead className={clsx(styles.tableHead, className)} {...rest}>
    {children}
  </thead>
);

const MarkdownTh = ({ children, className, node: _node, ...rest }: ComponentPropsWithoutRef<'th'> & ExtraProps) => (
  <th className={clsx(styles.tableHeaderCell, className)} {...rest}>
    {children}
  </th>
);

const MarkdownTd = ({ children, className, node: _node, ...rest }: ComponentPropsWithoutRef<'td'> & ExtraProps) => (
  <td className={clsx(styles.tableCell, className)} {...rest}>
    {children}
  </td>
);

// Block code wrapper — pass through when language-highlighted (SyntaxHighlighter
// renders its own <pre>), otherwise provide the styled block container.
const MarkdownPre = ({ children }: ComponentPropsWithoutRef<'pre'> & ExtraProps) => {
  const childArray = Children.toArray(children);
  const hasLanguage = childArray.some((child) => {
    if (!isValidElement(child)) {
      return false;
    }
    const { className } = child.props as { className?: string };
    return typeof className === 'string' && /language-\w+/.test(className);
  });

  if (hasLanguage) {
    return <>{children}</>;
  }

  return (
    <div className={styles.preBlock}>
      <pre>{children}</pre>
    </div>
  );
};

// Fenced code: soft nest + quiet token theme (CSS variables, not Prism coy).
const MarkdownCode = ({ className, children, node: _node, ...rest }: ComponentPropsWithoutRef<'code'> & ExtraProps) => {
  const match = className?.match(/language-(\w+)/);
  const codeString = String(children).replace(/\n$/, '');

  if (match) {
    return (
      <div className={styles.codeBlock}>
        <SyntaxHighlighter
          language={match[1]}
          style={quietPrismTheme}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: 10,
            lineHeight: 1.4,
          }}
          codeTagProps={{
            style: {
              fontFamily: 'var(--lp-font-family-monospace, monospace)',
              fontSize: 10,
              lineHeight: 1.4,
            },
          }}
          wrapLongLines
        >
          {codeString}
        </SyntaxHighlighter>
      </div>
    );
  }

  // Inline code and plain block code — styled via .renderer code / .renderer pre code in CSS module
  return (
    <code className={className} {...rest}>
      {children}
    </code>
  );
};

// Links — handle session:// protocol for session IDs and external source URLs
const MarkdownLink = ({ href, children, node: _node, ...rest }: ComponentPropsWithoutRef<'a'> & ExtraProps) => {
  const { projectKey, artifactsEnabled, callbacks } = useMarkdownRuntime();
  const artifactLookup = useArtifactLookup();

  if (artifactsEnabled && href?.startsWith(VEGA_ARTIFACT_LINK_PROTOCOL)) {
    const parsed = parseArtifactHref(href);
    if (parsed) {
      const resolvedProjectKey = artifactLookup?.projectKey ?? projectKey;
      const entry = artifactLookup?.lookup(parsed.type, parsed.externalId);
      if (entry) {
        return (
          <ArtifactChip
            projectKey={resolvedProjectKey}
            type={parsed.type}
            externalId={parsed.externalId}
            title={entry.title}
            status={entry.status}
            url={entry.url}
            variant="inline"
          />
        );
      }
      if (callbacks.renderArtifactToken) {
        return (
          <>
            {callbacks.renderArtifactToken({
              projectKey: resolvedProjectKey,
              type: parsed.type,
              externalId: parsed.externalId,
            })}
          </>
        );
      }
      return (
        <ArtifactChip
          projectKey={resolvedProjectKey}
          type={parsed.type}
          externalId={parsed.externalId}
          variant="inline"
          placeholder
        />
      );
    }
  }

  if (href?.startsWith(SESSION_LINK_PROTOCOL)) {
    const sessionId = href.slice(SESSION_LINK_PROTOCOL.length);
    const sessionUrl = toSession({ projectKey, sessionId });
    return (
      <a
        href={sessionUrl.pathname}
        className={styles.sessionLink}
        title={msg('observability.markdown.openSessionTitle')}
        target="_blank"
        rel="noopener noreferrer"
        {...rest}
      >
        {children}
      </a>
    );
  }

  const externalSource = matchVegaExternalSourceUrl(href);
  const { onExternalSourceClick } = callbacks;
  if (externalSource && onExternalSourceClick) {
    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      onExternalSourceClick(externalSource);
    };
    return (
      <a
        href={externalSource.url}
        className={styles.sourceLink}
        onClick={handleClick}
        title={msg('observability.markdown.continueInVegaTitle')}
        {...rest}
      >
        {children}
      </a>
    );
  }

  // Generic external link. When the consumer supplies an allow-list policy, render
  // disallowed destinations as inert text so attacker-influenceable content can't link the
  // user to an arbitrary site. (react-markdown's urlTransform has already neutralized
  // dangerous schemes like javascript:, so this guards the host/destination.)
  if (callbacks.isExternalUrlAllowed && !callbacks.isExternalUrlAllowed(href)) {
    return (
      <span className={styles.blockedLink} title={msg('observability.markdown.blockedLinkTitle')}>
        {children}
      </span>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
};

// Task list checkboxes — index from rehypeTaskListIndices (parse phase, not render)
const MarkdownInput = ({
  type,
  checked: defaultChecked,
  disabled: _disabled,
  node,
  ...rest
}: ComponentPropsWithoutRef<'input'> & ExtraProps) => {
  const { callbacks, checkedTasks, setTaskChecked } = useMarkdownRuntime();

  if (type !== 'checkbox') {
    return <input type={type} {...rest} />;
  }
  const index = parseTaskIndex(node);
  if (index < 0) {
    return <input {...rest} type="checkbox" disabled className={styles.taskListInput} />;
  }
  const label = parseTaskLabel(node);
  const isChecked = !!(checkedTasks[index] ?? defaultChecked ?? false);
  return (
    <input
      {...rest}
      type="checkbox"
      checked={isChecked}
      className={styles.taskListInput}
      disabled={!callbacks.onTaskAction}
      onChange={(event) => {
        const next = event.target.checked;
        setTaskChecked(index, next);
        callbacks.onTaskAction?.({ action: 'toggle', index, label });
      }}
    />
  );
};

// Module-level and therefore permanently stable: react-markdown compares component identities to
// decide whether an element type changed, and a new identity means unmount + remount of the whole
// rendered message. Nothing here may be rebuilt per render.
const components: Components = {
  table: MarkdownTable,
  thead: MarkdownThead,
  th: MarkdownTh,
  td: MarkdownTd,
  pre: MarkdownPre,
  code: MarkdownCode,
  a: MarkdownLink,
  input: MarkdownInput,
};

// Preserve our custom protocols (session:// for session links, vega-artifact:// for artifact
// chips). react-markdown's default urlTransform strips anything outside http/https/mailto/tel,
// which would zero out the href before MarkdownLink can see it.
const urlTransform = (value: string) => {
  if (value.startsWith(SESSION_LINK_PROTOCOL) || value.startsWith(VEGA_ARTIFACT_LINK_PROTOCOL)) {
    return value;
  }
  return defaultUrlTransform(value);
};

// Simple markdown renderer component using react-markdown with GFM support
export const MarkdownRenderer: FC<MarkdownRendererProps> = ({
  text,
  projectKey,
  onExternalSourceClick,
  artifactsEnabled = false,
  renderArtifactToken,
  onTaskAction,
  isExternalUrlAllowed,
}) => {
  const [checkedTasks, setCheckedTasks] = useState<Record<number, boolean>>({});

  // Only the reset effect below reads this, and only after commit — which is why it is safe to
  // assign in an effect. Depending on `onTaskAction` directly would re-run the effect (wiping the
  // user's checkbox selections) every time the parent re-rendered with a fresh inline callback.
  const onTaskActionRef = useRef(onTaskAction);
  useLayoutEffect(() => {
    onTaskActionRef.current = onTaskAction;
  });

  useEffect(() => {
    // Returning `prev` when there is nothing to clear keeps the identity stable, so React bails out
    // of the update entirely. Writing a fresh `{}` on every streamed token re-rendered the whole
    // renderer for no change — the jank this component is trying to avoid.
    setCheckedTasks((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    onTaskActionRef.current?.({ action: 'reset' });
  }, [text]);

  const setTaskChecked = useCallback((index: number, checked: boolean) => {
    setCheckedTasks((prev) => ({ ...prev, [index]: checked }));
  }, []);

  const runtime = useMemo<MarkdownRuntime>(
    () => ({
      projectKey,
      artifactsEnabled,
      callbacks: { onExternalSourceClick, renderArtifactToken, onTaskAction, isExternalUrlAllowed },
      checkedTasks,
      setTaskChecked,
    }),
    [
      projectKey,
      artifactsEnabled,
      onExternalSourceClick,
      renderArtifactToken,
      onTaskAction,
      isExternalUrlAllowed,
      checkedTasks,
      setTaskChecked,
    ],
  );

  const processedText = artifactsEnabled
    ? preprocessArtifactTokens(preprocessSessionIds(stripHiddenDirectives(text)))
    : preprocessSessionIds(stripHiddenDirectives(text));

  return (
    <div className={styles.renderer}>
      <MarkdownRuntimeContext.Provider value={runtime}>
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          components={components}
          urlTransform={urlTransform}
        >
          {processedText}
        </ReactMarkdown>
      </MarkdownRuntimeContext.Provider>
    </div>
  );
};
